import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { PartyPopper, ScrollText, Home, Share2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { getProductById } from "../data/products";
import Coin from "../components/Coin";
import BragCard from "../components/BragCard";
import sfx from "../utils/sound";

const Checkout = () => {
  const { cart, coins, checkout, name } = useStore();
  const { width, height } = useWindowSize();
  const nav = useNavigate();
  const didCheckoutRef = useRef(false);
  const [order, setOrder] = useState(null);
  const [confettiOn, setConfettiOn] = useState(true);
  const [bragOpen, setBragOpen] = useState(false);

  const items = useMemo(
    () =>
      cart
        .map((c) => {
          const p = getProductById(c.id);
          return p ? { id: p.id, name: p.name, emoji: p.emoji, price: p.price, qty: c.qty } : null;
        })
        .filter(Boolean),
    [cart]
  );
  const total = items.reduce((s, it) => s + it.price * it.qty, 0);

  useEffect(() => {
    if (didCheckoutRef.current) return;
    if (items.length === 0) return;
    if (total > coins) return;
    didCheckoutRef.current = true;
    const o = checkout(items, total);
    setOrder(o);
    sfx.fanfare();
    const t = setTimeout(() => setConfettiOn(false), 6000);
    return () => clearTimeout(t);
  }, [items, total, coins, checkout]);

  if (!order && items.length === 0) {
    return <Navigate to="/cart" replace />;
  }
  if (!order && total > coins) {
    return <Navigate to="/cart" replace />;
  }

  return (
    <div
      className="min-h-[calc(100vh-80px)] dots-bg px-4 py-8 flex items-start justify-center"
      data-testid="checkout-page"
    >
      {confettiOn && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={280}
          recycle={false}
          gravity={0.22}
          colors={["#FF5A5F", "#FFB400", "#00A699", "#F368E0", "#54A0FF", "#5F27CD"]}
        />
      )}

      <div
        className="chunky-card pop-in w-full max-w-lg p-6 sm:p-8"
        style={{ background: "#fff" }}
      >
        <div className="text-center">
          <div className="text-6xl bob mb-2" aria-hidden="true">🎉</div>
          <h1
            className="font-display text-3xl sm:text-4xl leading-tight"
            style={{ color: "#00A699" }}
            data-testid="checkout-success-title"
          >
            Woohoo, {name || "friend"}!
          </h1>
          <p className="mt-2 text-lg" style={{ color: "#484848" }}>
            Your pretend purchase is complete!
          </p>
        </div>

        {/* Receipt */}
        <div
          className="mt-6 p-5 rounded-2xl"
          style={{
            background:
              "repeating-linear-gradient(180deg, #FFF9E6 0 26px, #fff3c2 26px 27px)",
            border: "3px dashed #2b2b2b",
          }}
          data-testid="checkout-receipt"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-xl inline-flex items-center gap-2">
              <ScrollText size={20} strokeWidth={3} />
              Receipt
            </div>
            <div className="text-xs" style={{ color: "#484848" }}>
              {order && new Date(order.date).toLocaleString()}
            </div>
          </div>
          <ul className="flex flex-col gap-2">
            {(order?.items || items).map((it) => (
              <li
                key={it.id}
                className="flex items-center gap-3"
                data-testid={`receipt-item-${it.id}`}
              >
                <span className="text-2xl" aria-hidden="true">{it.emoji}</span>
                <span className="flex-1 font-bold">
                  {it.name}{" "}
                  <span style={{ color: "#484848" }}>× {it.qty}</span>
                </span>
                <span
                  className="inline-flex items-center gap-1 font-display"
                >
                  <Coin size={16} /> {it.price * it.qty}
                </span>
              </li>
            ))}
          </ul>
          <div
            className="mt-4 pt-3 flex items-center justify-between font-display text-lg"
            style={{ borderTop: "2px dashed #2b2b2b" }}
          >
            <span>Total spent</span>
            <span className="inline-flex items-center gap-1" data-testid="receipt-total">
              <Coin size={20} /> {order?.total ?? total}
            </span>
          </div>
          <div
            className="mt-2 flex items-center justify-between text-base"
            style={{ color: "#484848" }}
          >
            <span>Coins remaining</span>
            <span className="inline-flex items-center gap-1" data-testid="receipt-coins-after">
              <Coin size={16} /> {order?.coinsAfter ?? Math.max(0, coins - total)}
            </span>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            className="btn-3d btn-pink flex-1"
            onClick={() => {
              sfx.click();
              setBragOpen(true);
            }}
            data-testid="checkout-brag-btn"
          >
            <Share2 size={20} strokeWidth={3} />
            Brag Card
          </button>
          <Link
            to="/orders"
            className="btn-3d btn-accent flex-1"
            data-testid="checkout-view-orders-btn"
          >
            <PartyPopper size={20} strokeWidth={3} />
            My Adventures
          </Link>
          <button
            className="btn-3d flex-1"
            onClick={() => nav("/shop")}
            data-testid="checkout-continue-btn"
          >
            <Home size={20} strokeWidth={3} />
            Keep Playing
          </button>
        </div>
      </div>

      <BragCard
        open={bragOpen}
        onClose={() => setBragOpen(false)}
        title={`${name || "I"} went shopping!`}
        subtitle={`Grabbed ${(order?.items || items).reduce((s, it) => s + it.qty, 0)} treats on 67Cafe`}
        emoji="🛍️"
        coins={order?.total ?? total}
        name={name || "67Cafe Kid"}
      />
    </div>
  );
};

export default Checkout;
